import { DesignerSetting } from '@app/DesignerSetting';
import { LoginModal } from '@app/page-components';
import {
  BehaviorSubject,
  HttpStatusCode,
  IframeMessageToChild,
  IframeMessageToChildType,
  IframeMessageToParent,
  IframeMessageToParentType,
  Public,
  Subject,
  User,
  UserClientId,
  UserCoreRole
} from '@app/types';
import { createUuid } from '@app/utilities';
// This import cannot be shortened without creating circular dependencies.
import { sendIframeMessageToParent } from '@app/utilities/functions/iframe';
// This import cannot be shortened without creating circular dependencies.
import { getLocalStorage, setLocalStorage } from '@app/utilities/functions/local-storage';
import { IErrorMessageService } from './error-message-service';
import { IGlobalModalsService } from './global-modals-service';
import { IHttpService } from './http-service';
import {
  HttpMethod,
  HttpFetchProps,
  HttpAdapter,
  UserLoginProps,
  UserMfaQr,
  UserMfaToken,
  UserMfaValidate,
  UserRaw,
  HttpResponseProps,
  UserMfaSettings
} from './types';

const isIFrame = window.top !== window.self;

/** Parts of the user that are present in the token itself. */
type TokenUser = Pick<User, (
  'access_token'
  | 'authorities'
  | 'expires'
  | 'expires_in'
  | 'refresh_token'
  | 'role'
  | 'scope'
  | 'token_type'
)>;

/** Public interface of @see UserService. */
export type IUserService = Public<UserService>;

/** Service that should be used for user authentication. */
export class UserService implements HttpAdapter {
  private static readonly userKey = 'user';
  readonly designRoles = new BehaviorSubject(new Set<UserCoreRole>());
  readonly userChange = new BehaviorSubject<User | null>(this.user);
  private _designRolesFetchToken: string| null = null;
  private _errorMessageService: IErrorMessageService;
  private _globalModalsService: IGlobalModalsService;
  private _httpService: IHttpService;
  private _authMessageReceived = new Subject<IframeMessageToChild>();
  private _rolesHttpService: IHttpService;
  /** Used to only allow one active @see tryRefreshingUser call at a time. */
  private _userRefreshAttempt: Promise<boolean> | null = null;

  /** Whether the user is logged in or not. */
  get isLoggedIn(): boolean {
    const { user } = this;
    return (user?.expires ?? 0) >= Date.now();
  }

  /** Get the currently logged in user. */
  get user(): User | null {
    return getLocalStorage<User>(UserService.userKey);
  }
  set user(user: User | null) {
    setLocalStorage<User | null>(UserService.userKey, user);
    this.userChange.next(user);
    if (!this.isLoggedIn) {
      this.tryRefreshingUser();
    }
    this.refreshDesignRoles();
  }

  /** Gets an ID to associate with the current machine and stores it in local storage for MFA. */
  private get _macAddress(): string {
    return getLocalStorage('mac-address', createUuid, { setOnFallback: true });
  }

  constructor(
    errorMessageService: IErrorMessageService,
    globalModalsService: IGlobalModalsService,
    httpService: IHttpService,
    rolesHttpService: IHttpService
  ) {
    this._errorMessageService = errorMessageService;
    this._globalModalsService = globalModalsService;
    this._httpService = httpService;
    this._rolesHttpService = rolesHttpService;
    this._onIframeMessage = this._onIframeMessage.bind(this);
    window.addEventListener('message', this._onIframeMessage);
    if (isIFrame) {
      // Clear local storage in favor of getting a new user from parent.
      this.user = null;
    }
    if (!this.isLoggedIn) {
      this.tryRefreshingUser();
    }
  }

  /** Modify the request to add the 'Authorization' header. */
  async adaptHttpRequest(request: HttpFetchProps): Promise<HttpFetchProps> {
    if (!this.isLoggedIn) {
      const shouldRetry = await this.tryRefreshingUser();
      if (!shouldRetry) {
        const error = new Error('Unable to refresh access token. Please refresh and log back in.');
        this._errorMessageService.addError(error);
        throw error;
      }
    }

    const user = this.user;
    if (user) {
      request.headers ??= {};
      request.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return request;
  }

  /** Handle unauthorized errors. */
  async adaptHttpResponse({
    request,
    response,
    service
  }: HttpResponseProps): Promise<Response> {
    if (
      response.status === HttpStatusCode.unauthorized
      && await this.tryRefreshingUser()
    ) {
      return await service.fetch(request);
    }
    return response;
  }

  /** Check authentication settings to determine the next step of login. */
  async getAuthenticationSettings({
    password,
    username
  }: Pick<UserLoginProps, 'password' | 'username'>): Promise<UserMfaSettings> {
    const {
      enabled2FA: isMfaEnabled,
      rememberMe2FA: isRemembered,
      validUsernameAndPassword
    } = await this._httpService.fetchJsonOrFail<UserMfaValidate>({
      body: this._httpService.createMultipartForm({ password }),
      headers: { macAddress: this._macAddress },
      method: HttpMethod.post,
      url: `management/2fa/${username}/validate-password`
    });

    if (!validUsernameAndPassword) {
      return { validUsernameAndPassword };
    } else if (isMfaEnabled) {
      return {
        isMfaEnabled,
        isRemembered,
        validUsernameAndPassword
      };
    }

    return {
      isMfaEnabled,
      isRemembered: false,
      validUsernameAndPassword,
      ...await this._httpService.fetchJsonOrFail<UserMfaQr>({
        url: `management/2fa/${username}/qrcode`
      })
    };
  }

  /** Check if the user has a given role for the open @see Design. */
  hasDesignRole(roles: Set<UserCoreRole>, role: UserCoreRole | UserCoreRole[], every?: boolean): boolean {
    const { designId, version } = window?.apm?.serialized ?? {};
    if (
      roles.has(UserCoreRole.admin)
      || roles.has(UserCoreRole.designerAdmin)
      // Allow all actions if the user has no roles at all,
      // until the API is proven to be stable in PROD
      || roles.size === 0
      // Allow all actions if the design hasn't been saved yet
      || !designId
      || version === '1.0.0'
    ) {
      return true;
    }

    const rolesArray = (Array.isArray(role)) ? role : [role];
    return (every) ? rolesArray.every((r) => roles.has(r)) : (
      rolesArray.some((r) => roles.has(r))
    );
  }

  /** Login user in, enabling multi-factor authentication if it is not already on. */
  async login({
    code,
    enableRememberMe,
    isRemembered,
    isMfaEnabled,
    password,
    secret,
    username
  }: UserLoginProps): Promise<User> {
    const endpoint = (isMfaEnabled) ? 'token' : 'enable';
    const url = `management/2fa/${username}/${endpoint}`;
    const body = this._httpService.createUrlForm({
      ...((!isRemembered) ? { code } : {}),
      password,
      ...((!isMfaEnabled) ? { secret } : {})
    });

    const token = await this._httpService.fetchJsonOrFail<UserMfaToken>({
      body,
      headers: { macAddress: this._macAddress },
      method: HttpMethod.post,
      url
    });

    const userRaw = await this._httpService.fetchJsonOrFail<UserRaw>({
      headers: {
        Authorization: `Bearer ${token.tokenResponse.access_token}`
      },
      url: 'user'
    });

    const user: User = {
      clientId: UserClientId.d2,
      mail: userRaw.email,
      fullName: userRaw.fullName,
      refreshCount: 0,
      username: userRaw.name,
      ...this._convertToken(token)
    };

    if (
      enableRememberMe
      && !isRemembered
      && code
    ) {
      await this._httpService.fetch({
        body: this._httpService.createMultipartForm({
          password,
          code,
          macAddress: this._macAddress
        }),
        method: HttpMethod.post,
        url: `management/2fa/${username}/remember-me`
      });
    }

    this.user = user;
    return user;
  }

  /** Log the current user out. */
  logout(): void {
    this.user = null;
    window.location.href = `/${location.search}`;
  }

  async refreshDesignRoles(): Promise<void> {
    const { access_token } = this.user ?? {};
    const { designId } = window?.apm?.serialized ?? {};
    if (!access_token || !designId) {
      this.designRoles.next(new Set());
      this._designRolesFetchToken = null;
      return;
    }

    const token = `${access_token};${designId}`;
    if (token === this._designRolesFetchToken) {
      return;
    }

    this._designRolesFetchToken = token;
    const roles = await this._rolesHttpService.fetchJsonOrFail<UserCoreRole[]>({
      headers: {
        Authorization: `Bearer ${this.user?.access_token}`
      },
      url: `roles-for/design/${designId}`
    });
    if (token === this._designRolesFetchToken) {
      this.designRoles.next(new Set(roles));
    }
  }

  /**
   * Try refreshing the user's token if the request fails.
   *
   * @returns {Promise<boolean>} whether the request should be retried or not.
   */
  async tryRefreshingUser(): Promise<boolean> {
    if (this._userRefreshAttempt) {
      return this._userRefreshAttempt;
    }

    this._userRefreshAttempt = (async (): Promise<boolean> => {
      if (isIFrame) {
        // Get authentication from parent.
        return new Promise((resolve) => {
          const onAuthMessage = async () => {
            this._userRefreshAttempt = null;
            this._authMessageReceived.unsubscribe(onAuthMessage);
            let isLoggedIn = this.isLoggedIn;
            try {
              // Hit an API to make sure token is good since sometimes the token is not expired,
              // but has been invalidated due to refresh token being used.
              await this.refreshDesignRoles();
            } catch {
              isLoggedIn = false;
            }
            resolve(isLoggedIn);
          };
          this._authMessageReceived.subscribe(onAuthMessage);
          sendIframeMessageToParent({
            type: IframeMessageToParentType.requestAuth
          });
        });
      }

      // Login without username & password to use refresh token.
      const { authTokenRefreshCountLimit } = DesignerSetting.s;
      const { user } = this;
      const refreshCount = user?.refreshCount ?? 0;
      const refreshToken = (user && refreshCount < authTokenRefreshCountLimit) ? (
        user.refresh_token
      ) : null;

      if (user && refreshToken) {
        const { value: newToken } = await this._httpService.fetchJson<UserMfaToken>({
          body: this._httpService.createUrlForm({
            accessToken: user.access_token,
            refreshToken
          }),
          method: HttpMethod.post,
          url: 'user/refresh/token'
        });
        if (newToken) {
          this.user = {
            ...user,
            ...this._convertToken(newToken),
            refreshCount: refreshCount + 1
          };
        } else {
          this.user = null;
        }

        if (this.isLoggedIn) {
          this._userRefreshAttempt = null;
          return true;
        }
      }

      // Have the user manually log back in.
      return new Promise<boolean>((resolve) => {
        this._globalModalsService.activeDialog = (
          <LoginModal
            onComplete={async () => {
              this._userRefreshAttempt = null;
              const { isLoggedIn } = this;
              if (!isLoggedIn) {
                this.logout();
              }
              resolve(isLoggedIn);
              this._globalModalsService.activeDialog = null;
            }}
          />
        );
      });
    })();

    return this._userRefreshAttempt;
  }

  /** Convert the token part of logging in and refreshing access tokens. */
  private _convertToken(token: UserMfaToken): TokenUser {
    return {
      access_token: token.tokenResponse.access_token,
      authorities: token.userRoles?.map((role) => ({ authority: role })) ?? [],
      expires: Date.now() + token.tokenResponse.refresh_expires_in * 1000,
      expires_in: token.tokenResponse.expires_in,
      refresh_token: token.tokenResponse.refresh_token,
      role: token.userRoles,
      scope: token.tokenResponse.scope,
      token_type: token.tokenResponse.token_type
    };
  }

  private async _onIframeMessage(
    event: MessageEvent<IframeMessageToChild | IframeMessageToParent>
  ): Promise<void> {
    // Ignore messages from child iframes (e.g. Agentic) so they cannot overwrite auth state.
    if (event.source !== window.parent) {
      return;
    }
    const {
      payload,
      type
    } = event.data;

    if (type === IframeMessageToChildType.auth) {
      this.user = payload;
      if (this.isLoggedIn) {
        sendIframeMessageToParent({
          type: IframeMessageToParentType.success
        });
      } else {
        console.error(payload);
        const error = 'Invalid token provided, please refresh the page and try again.';
        this._errorMessageService.addError(error);
        sendIframeMessageToParent({
          payload: error,
          type: IframeMessageToParentType.error
        });
      }
      this._authMessageReceived.next(event.data);
    } else if (type === IframeMessageToParentType.requestAuth) {
      if (!this.isLoggedIn && !await this.tryRefreshingUser()) {
        this._errorMessageService.addError('Failed to refresh access token.');
      } else {
        this.userChange.next(this.user);
      }
    } else if (type === IframeMessageToParentType.error) {
      this._errorMessageService.addError(payload);
    } else if (type === IframeMessageToParentType.success) {
      // Intentionally left blank.
    } else {
      // These actually do happen, but we still want TypeScript to force us to implements the ones we know about.
      const _invalidType: never = type;
    }
  }
}
